
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Check, X, Edit } from "lucide-react";

interface EditablePathInputProps {
  label: string;
  currentPath: string;
  onPathChange: (path: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const EditablePathInput = ({
  label,
  currentPath,
  onPathChange,
  placeholder = "Enter file path or URL",
  disabled = false,
}: EditablePathInputProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempPath, setTempPath] = useState(currentPath);

  const handleStartEdit = () => {
    setTempPath(currentPath);
    setIsEditing(true);
  };

  const handleSave = () => {
    onPathChange(tempPath);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempPath(currentPath);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <Input
              value={tempPath}
              onChange={(e) => setTempPath(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1"
              autoFocus
              disabled={disabled}
            />
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={disabled}
            >
              <Check className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <Input
              value={currentPath}
              readOnly
              className="flex-1 bg-gray-50"
              placeholder={placeholder}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleStartEdit}
              disabled={disabled}
            >
              <Edit className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
